import { array, mixed, object, string } from 'yup';

import { getQuillPlainText } from '../../../../../components/baseV2';
import {
  ASSESSMENT_BEHAVIOR_TEXT_MIN_LENGTH,
  ASSESSMENT_METHOD_RICH_TEXT_MIN_LENGTH,
  ASSESSMENT_METHOD_VISUAL_TITLE_MIN_LENGTH,
  ASSESSMENT_READINESS_NOTES_MIN_LENGTH,
} from './constants';

import type { Moment } from 'moment';

function requiredRichTextMinLength(minLength: number) {
  return string()
    .required()
    .test(
      'required-min-plain-text',
      `Must be at least ${minLength} characters`,
      (value) => getQuillPlainText(value ?? '').trim().length >= minLength,
    );
}

export const ASSESSMENT_BEHAVIOR_FORM_SCHEMA = object({
  behavior: string()
    .trim()
    .required()
    .min(
      ASSESSMENT_BEHAVIOR_TEXT_MIN_LENGTH,
      `Must be at least ${ASSESSMENT_BEHAVIOR_TEXT_MIN_LENGTH} characters`,
    ),
  dsm5_symptom: string().trim().required(),
  parent_concerns: string()
    .trim()
    .required()
    .min(
      ASSESSMENT_BEHAVIOR_TEXT_MIN_LENGTH,
      `Must be at least ${ASSESSMENT_BEHAVIOR_TEXT_MIN_LENGTH} characters`,
    ),
});

export const ASSESSMENT_INFO_FORM_SCHEMA = object({
  initial_assessment_date: mixed<Moment>().nullable().required(),
  diagnostic_report_reviewed: string().required(),
  reassessment_date: mixed<Moment>().nullable().required(),
  parent_interview_summary: string().test(
    'required-plain-text',
    'Required',
    (value) => getQuillPlainText(value ?? '').trim().length > 0,
  ),
  additional_files_viewed: array().of(string().defined()),
});

export const ASSESSMENT_METHOD_FORM_SCHEMA = object({
  tool: string().trim().required(),
  assessment_date: mixed<Moment>().nullable().required(),
  rationale: requiredRichTextMinLength(ASSESSMENT_METHOD_RICH_TEXT_MIN_LENGTH),
  selection_rationale: requiredRichTextMinLength(ASSESSMENT_METHOD_RICH_TEXT_MIN_LENGTH),
  tool_description: requiredRichTextMinLength(ASSESSMENT_METHOD_RICH_TEXT_MIN_LENGTH),
  client_score: requiredRichTextMinLength(ASSESSMENT_METHOD_RICH_TEXT_MIN_LENGTH),
});

export const ASSESSMENT_METHOD_VISUAL_FORM_SCHEMA = object({
  title: string()
    .trim()
    .required()
    .min(
      ASSESSMENT_METHOD_VISUAL_TITLE_MIN_LENGTH,
      `Must be at least ${ASSESSMENT_METHOD_VISUAL_TITLE_MIN_LENGTH} characters`,
    ),
  analysis: requiredRichTextMinLength(ASSESSMENT_METHOD_RICH_TEXT_MIN_LENGTH),
});

export const ASSESSMENT_READINESS_DATE_FORM_SCHEMA = object({
  telehealth_assessment_date: mixed<Moment>().nullable().required(),
});

export const ASSESSMENT_READINESS_NOTES_FORM_SCHEMA = object({
  notes: string()
    .trim()
    .required()
    .min(
      ASSESSMENT_READINESS_NOTES_MIN_LENGTH,
      `Must be at least ${ASSESSMENT_READINESS_NOTES_MIN_LENGTH} characters`,
    ),
});

export const ASSESSMENT_SUMMARY_FORM_SCHEMA = object({
  assessment_results: string().test(
    'required-plain-text',
    'Required',
    (value) => getQuillPlainText(value ?? '').trim().length > 0,
  ),
  priorities: string().test(
    'required-plain-text',
    'Required',
    (value) => getQuillPlainText(value ?? '').trim().length > 0,
  ),
});

export const ASSESSMENT_OBSERVATIONS_FORM_SCHEMA = object({
  observations: string().trim().required(),
});
